'use client';

import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Button as AriaButton } from 'react-aria-components';

import Icon from '../Icon/Icon';
import Loader from '../Loader/Loader';
import { useFusionTheme } from '../hooks';

import styles from './Button.module.scss';
import { Slot } from './Slot';

export type ButtonProps = AriaButtonProps &
  VariantProps<typeof buttonVariants> & {
    iconPosition?: 'left' | 'right';
    icon?: React.FC<React.SVGProps<SVGElement>>;
    asChild?: true;
  };

const buttonVariants = cva(styles.button, {
  variants: {
    variant: {
      primary: styles.variantPrimary,
      secondary: styles.variantSecondary,
      tertiary: styles.variantTertiary
    },
    size: {
      small: styles.sizeSmall,
      medium: styles.sizeMedium,
      large: styles.sizeLarge
    },
    iconPosition: {
      right: styles.reverse
    },
    width: {
      full: styles.full,
      hug: ''
    }
  }
});

const Button = forwardRef<HTMLButtonElement, React.PropsWithChildren<ButtonProps>>(
  (
    {
      variant = 'primary',
      size = 'medium',
      iconPosition,
      icon,
      className,
      width = 'hug',
      children,
      asChild,
      ...buttonProps
    },
    forwardedRef
  ) => {
    const { addThemeClassName } = useFusionTheme();

    const Component = asChild ? Slot : AriaButton;

    return (
      <Component
        ref={forwardedRef}
        className={addThemeClassName(buttonVariants({ variant, size, iconPosition }), className)}
        {...buttonProps}
      >
        {!buttonProps.isPending && icon && <Icon icon={icon} aria-hidden />}
        {buttonProps.isPending ? <Loader /> : children}
      </Component>
    );
  }
);

export default Button;
